/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {
    Stack,
    Box,
    Skeleton,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import {useGlobaleContext} from '../../contexts/globale'
import {getPriceData} from '../../utils/product-utils'
import DisplayPrice from '../display-price'

const PricePerItem = ({currency, basePrice}) => {
    return (
        <Text fontSize={{base: '12px', lg: '14px'}}>
            <FormattedNumber style="currency" currency={currency} value={basePrice} />
            <FormattedMessage
                defaultMessage="ea"
                id="price_per_item.label.each"
                description="Abbreviated 'each', follows price per item, like $10/ea"
            />
        </Text>
    )
}

PricePerItem.propTypes = {
    currency: PropTypes.string.isRequired,
    basePrice: PropTypes.number
}

/**
 * In the context of a cart product item variant, this component renders the item's
 * pricing, taking into account applied discounts as well as base item prices.
 */
const ItemPrice = ({
    currency,
    align = 'right',
    baseDirection = 'column',
    productItem,
    currencySymbol,
    ...props
}) => {
    const {currencyCode} = useGlobaleContext()
    const cartStyles = useMultiStyleConfig('CartStyles')
    const variant = useItemVariant()
    const intl = useIntl()
    if (variant && productItem?.quantity) {
        variant.quantity = productItem?.quantity // please use the live quantity, rather than using the old one from 'variant'
    }
    const quantity = variant?.quantity || 1
    let priceData = getPriceData(variant, {quantity})

    const hasMarkdown =
        variant?.tieredPrices?.[0]?.price &&
        variant?.tieredPrices?.[1]?.price &&
        variant?.tieredPrices?.[0]?.price != variant?.tieredPrices?.[1]?.price

    // Three-tiered from product-level promotions (markdown + promo with promotionalPrice)
    const isPromoThreeTiered = !!(
        hasMarkdown && variant?.productPromotions?.some((promo) => promo?.promotionalPrice)
    )

    // Three-tiered from BMSM basket-level adjustments (markdown + BMSM priceAdjustment)
    const bmsmAdjustment = variant?.priceAdjustments?.find((adj) =>
        adj?.promotionId?.includes('BMSM')
    )
    const bmsmAdjustedUnitPrice =
        bmsmAdjustment && variant?.priceAfterItemDiscount != null && quantity
            ? variant.priceAfterItemDiscount / quantity
            : null
    const isBmsmThreeTiered = !!(
        hasMarkdown &&
        bmsmAdjustedUnitPrice != null &&
        bmsmAdjustedUnitPrice < priceData.currentPrice / quantity
    )

    const showThreeTieredPricing = isPromoThreeTiered || isBmsmThreeTiered

    if (showThreeTieredPricing) {
        priceData.listPrice = variant?.tieredPrices?.[1]?.price * quantity
    }
    if (isBmsmThreeTiered) {
        priceData.currentPrice = variant.priceAfterItemDiscount
        priceData.isOnSale = true
    }
    if (currencyCode && currencyCode !== currency) {
        return <Skeleton height="16px" width="49px" />
    }
    if (variant?.price === null) {
        return (
            <Box color="black.100" fontSize="14px" className="no-price">
                N/A
            </Box>
        )
    }

    if (variant?.c_charitySelection && variant?.c_gePriceAfterDiscount) {
        priceData.listPrice = variant.c_gePriceAfterDiscount
        priceData.currentPrice = variant.c_gePriceAfterDiscount
    }
    return (
        <Stack
            textAlign={align}
            direction={baseDirection}
            justifyContent={align === 'left' ? 'flex-start' : 'flex-end'}
            alignItems="baseline"
            spacing={priceData?.isOnSale ? 0 : 1}
            wrap="nowrap"
            className="product-prices-stack"
            {...props}
            {...cartStyles.lineItems.item.nameWrapper.price}
            sx={{
                '.list-price': {
                    color: showThreeTieredPricing ? 'green.720 !important' : 'gray.650 !important',
                    fontSize: showThreeTieredPricing ? '10px' : '14px',
                    fontWeight: '300',
                    letterSpacing: showThreeTieredPricing ? '0.05px' : '0.07px',
                    lineHeight: showThreeTieredPricing ? '25px' : '22px'
                },
                '.current-price': {
                    fontWeight: '700',
                    lineHeight: '22px',
                    color: 'black.100 !important',
                    letterSpacing: '0.07px',
                    fontSize: '14px'
                }
            }}
        >
            {variant?.itemId ? (
                <DisplayPrice
                    labelForA11y={variant?.name}
                    currency={currencyCode || currency}
                    priceData={priceData}
                    isLineItem={true}
                    showMarkdownText={showThreeTieredPricing}
                    symbol={currencySymbol}
                    listPriceProps={{fontSize: 'sm'}}
                />
            ) : (
                <DisplayPrice
                    labelForA11y={variant?.name}
                    currency={currencyCode || currency}
                    priceData={priceData}
                    isLineItem={true}
                    symbol={currencySymbol}
                    showMarkdownText={showThreeTieredPricing}
                    listPriceProps={{fontSize: 'sm'}}
                />
            )}
            {showThreeTieredPricing && (
                <Box width="full" display="inline-flex" marginBottom="-5px">
                    <Text
                        className="original-text"
                        {...cartStyles.lineItems.item.threeTierdPricing}
                    >
                        <FormattedMessage
                            id="total.original.price"
                            defaultMessage="Total Original Price"
                        />
                        &nbsp;
                    </Text>
                    <Text
                        textDecoration="line-through"
                        className="tiered-price"
                        {...cartStyles.lineItems.item.threeTierdPricing}
                    >
                        {intl
                            .formatNumber(variant?.tieredPrices?.[0]?.price * quantity, {
                                style: 'currency',
                                currency: variant?.c_selectedCurrency
                            })
                            ?.replace(
                                variant?.c_selectedCurrency,
                                variant?.c_selectedCurrencySymbol
                            )}
                        {/** Make the currency dynamic once we implement Globale prices. **/}
                    </Text>
                </Box>
            )}
        </Stack>
    )
}

ItemPrice.propTypes = {
    currency: PropTypes.string.isRequired,
    align: PropTypes.string,
    currencySymbol: PropTypes.string,
    productItem: PropTypes.object,
    baseDirection: PropTypes.string
}

export default ItemPrice
